import { useState, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import NextButton from "../components/NextButton";
import type { StepComponentType } from "../../hooks/IncorporationWizardFormContext";
import { useIncorporationWizardFormCtx } from "../../hooks/IncorporationWizardFormContext";
import TextInput from "../../components/TextInput";
import PhoneInput from "../../components/PhoneInput";
import SelectInput from "../../components/SelectInput";
import {
  GESTION_DIRECTORES,
  USER_TYPE,
  type IncorporationWizardInitialData,
  type UserTypeOption,
} from "@/schemas/incorporation";
import { CustomModal } from "@/components/CustomModal";
import countriesData from "../../components/countries.json";
import CheckboxInput from "../../components/CheckboxInput";
import { ContactCard } from "../../components/ContactCard";
import {
  createOrUpdateIncorporationContact,
  deleteIncorporationContact,
  IncorporationContactError,
} from "@/services/incorporationContact";
import { type ContactFormValues, contactFormSchema } from "../../schema";
import { isShareholderContact } from "../../contact-helper";
import z from "zod";

const shareholdersStepSchema = z
  .object({
    gestion_directores: z.enum(GESTION_DIRECTORES, {
      message: "shareholders.gestion_directores.required",
    }),
    // we validate contacts if is not abogado managed
    contacts: z.array(contactFormSchema).optional(),
    // Include user_type so we can branch validations
    user_type: z.enum(USER_TYPE).optional(),
  })
  .superRefine((data, ctx) => {
    const { contacts, user_type } = data;
    const isIntermediary = user_type === USER_TYPE[1]; // "Intermediario"

    if (!contacts) {
      ctx.addIssue({
        code: "custom",
        path: ["contacts"],
        message: "shareholders.validation.min_contacts",
      });
      return;
    }

    // 1) At least one soy_quien_completa_registro
    //    ONLY required for "Miembro", NOT for "Intermediario"
    if (!isIntermediary) {
      const hasCompleter =
        contacts &&
        contacts.some((c) => c?.soy_quien_completa_registro === true);
      if (!hasCompleter) {
        ctx.addIssue({
          code: "custom",
          path: ["contacts"],
          message: "shareholders.validation.completer_required",
        });
      }
    }

    // 2) Sum porcentaje_beneficio === 100
    const total = contacts.reduce(
      (sum, c) => sum + (c?.porcentaje_beneficio || 0),
      0
    );
    if (Math.abs(total - 100) > 0.01) {
      ctx.addIssue({
        code: "custom",
        path: ["contacts"],
        message: "shareholders.validation.percentage_sum",
      });
    }
  });

function ShareholderForm({
  initialData,
  userType,
  // gestionDirectores,
  onSave,
  onCancel,
  hideSoyQuienCompletaRegistro = false,
}: // disabledCargos = [],
{
  initialData?: ContactFormValues;
  userType?: UserTypeOption;
  // gestionDirectores?: string;
  onSave: (contact: ContactFormValues) => void;
  onCancel: () => void;
  hideSoyQuienCompletaRegistro?: boolean;
  // disabledCargos?: string[];
}) {
  const { t, i18n } = useTranslation();
  const isMember = userType === USER_TYPE[0]; // "Miembro"
  const { control, handleSubmit, watch, setValue } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: initialData || {
      contact_type: "persona natural",
      rol: ["accionista"],
      nombre: "",
      email: "",
      telefono: "",
      direccion: "",
      nacionalidades: [],
      nacionalidad_principal: "",
      cargo: "",
      porcentaje_beneficio: 0,
      residencia_fiscal: "",
      n_certificados: "",
      soy_quien_completa_registro: false,
    },
  });

  const contactType = watch("contact_type");
  const selectedNationalities = watch("nacionalidades") || [];

  // Auto-set nacionalidad_principal when exactly 1 nationality is selected
  useEffect(() => {
    if (selectedNationalities.length === 1) {
      setValue("nacionalidad_principal", selectedNationalities[0]);
    }
  }, [selectedNationalities, setValue]);

  const onSubmit = (formData: ContactFormValues) => {
    onSave(formData);
  };

  const countryOptions = countriesData.map((country) => ({
    value: country.name_es,
    label: i18n.language === "es" ? country.name_es : country.name_en,
  }));

  const shouldHideSoyQuienCompletaRegistro =
    !isMember ||
    (!initialData?.soy_quien_completa_registro && hideSoyQuienCompletaRegistro);

  return (
    <form onSubmit={handleSubmit(onSubmit, (errors) => console.log(errors))}>
      <div className="d-flex flex-column gap-3">
        {/* Show form only when persona natural is selected */}
        {contactType === "persona natural" && (
          <>
            <h5 className="fw-semibold size-18 mt-3">
              {t("shareholders.contact_form.persona_natural.title")}
            </h5>
            {/* Basic fields */}
            <TextInput
              name="nombre"
              control={control}
              label={t("shareholders.contact_form.nombre.label")}
              placeholder={t("shareholders.contact_form.nombre.placeholder")}
              helperText={t("shareholders.contact_form.nombre.helper_text")}
            />

            {/* Soy quien completa registro */}
            {!shouldHideSoyQuienCompletaRegistro && (
              <CheckboxInput
                name="soy_quien_completa_registro"
                control={control}
                label={t(
                  "shareholders.contact_form.soy_quien_completa_registro.label"
                )}
              />
            )}

            <div className="d-flex flex-column gap-3" style={{ maxWidth: 250 }}>
              <TextInput
                name="porcentaje_beneficio"
                control={control}
                label={t(
                  "shareholders.contact_form.porcentaje_beneficio.label"
                )}
                placeholder={t(
                  "shareholders.contact_form.porcentaje_beneficio.placeholder"
                )}
                type="number"
              />
            </div>

            <TextInput
              name="email"
              control={control}
              label={t("shareholders.contact_form.email.label")}
              placeholder={t("shareholders.contact_form.email.placeholder")}
            />

            <TextInput
              name="direccion"
              control={control}
              label={t("shareholders.contact_form.direccion.label")}
            />

            <PhoneInput
              name="telefono"
              control={control}
              label={t("shareholders.contact_form.telefono.label")}
              // placeholder={t("directors.contact_form.telefono.placeholder")}
            />

            <SelectInput
              name="nacionalidades"
              control={control}
              label={t("shareholders.contact_form.nacionalidades.label")}
              placeholder={t(
                "shareholders.contact_form.nacionalidades.placeholder"
              )}
              options={countryOptions}
              isMulti={true}
            />

            {selectedNationalities.length > 1 && (
              <SelectInput
                name="nacionalidad_principal"
                control={control}
                label={t(
                  "shareholders.contact_form.nacionalidad_principal.label"
                )}
                placeholder={t(
                  "shareholders.contact_form.nacionalidad_principal.placeholder"
                )}
                options={countryOptions.filter((option) =>
                  selectedNationalities.includes(option.value)
                )}
                isMulti={false}
              />
            )}
          </>
        )}

        {/* Action buttons */}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onCancel}
          >
            {t("shareholders.contact_form.buttons.cancel")}
          </button>
          <button type="submit" className="btn btn-black">
            {t("shareholders.contact_form.buttons.save")}
          </button>
        </div>
      </div>
    </form>
  );
}

/**
 * this step is diff than other bc is more complex maybe
 * maybe there is a way to use one schema and use useFilterArray from react-hook-form to filter the array of contacts
 * somehow if we want the modal to have his own form then submit to parent form, will have a nested form tag and thats is a nono
 * or maybe use react portal so modal can have his own form but submit to parent form
 */
const ShareholdersStep: StepComponentType = () => {
  const { t } = useTranslation();
  const { data, setData, next } = useIncorporationWizardFormCtx();

  const gestionDirectores = data?.incorporationRequest?.gestion_directores;

  // Transform contacts from global state to form values (porcentaje_beneficio as number)
  const contacts = useMemo<ContactFormValues[]>(
    () =>
      (data?.incorporationContact || [])
        .filter((contact) => isShareholderContact(contact))
        .map((contact) => ({
          ...contact,
          porcentaje_beneficio: contact.porcentaje_beneficio || 0,
        })),
    [data?.incorporationContact]
  );

  const [validationError, setValidationError] = useState<string | null>(null);
  const [contactError, setContactError] = useState<string | null>(null);
  console.log({contactsInShareholdersStep: data?.incorporationContact});
  // Handle edit contact - update via API
  // Uses contact ID to find the correct index in the full contacts array
  const handleEditContact =
    (contactId: string | number | undefined) => async (contactData: ContactFormValues) => {
      setContactError(null);

      try {
        // The incorporation request should already exist at this point
        const incorporationRequestId = data?.incorporationRequest?.id;

        if (!incorporationRequestId) {
          throw new Error(
            "La solicitud de incorporación no ha sido creada. Por favor, complete el paso anterior."
          );
        }

        const existingContacts = data?.incorporationContact || [];
        // Find the actual index in the full contacts array using the contact ID
        const actualIndex = existingContacts.findIndex((c) => c.id === contactId);
        const existingContact = actualIndex >= 0 ? existingContacts[actualIndex] : undefined;

        // Prepare data for API call
        const requestData = {
          ...contactData,
          incorporation_request_id: incorporationRequestId,
          ...(existingContact?.id && { id: existingContact.id }),
        };

        // Call API to create or update contact
        const response = await createOrUpdateIncorporationContact(requestData);

        // Update local state with response data
        setData((prevData) => {
          if (!prevData) return prevData;
          const updatedContacts = [...(prevData.incorporationContact || [])];
          // Find the actual index again in case state changed
          const indexToUpdate = updatedContacts.findIndex((c) => c.id === contactId);
          if (indexToUpdate >= 0) {
            updatedContacts[indexToUpdate] = response.data;
          }

          return {
            ...prevData,
            incorporationRequest: prevData.incorporationRequest || {},
            incorporationContact: updatedContacts,
            currentUser: prevData.currentUser,
            incorporationStripePrice: prevData.incorporationStripePrice,
            path: prevData.path,
          };
        });
      } catch (error) {
        // Handle errors but don't block the UI - contact remains in local state
        if (error instanceof IncorporationContactError) {
          const errorMessage =
            error.errors && error.errors.length > 0
              ? error.errors.join(", ")
              : error.message;
          setContactError(errorMessage);
        } else {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Error al guardar el contacto";
          setContactError(errorMessage);
        }
      }
    };

  // Handle append contact - create via API
  const handleAppendContact = async (contactData: ContactFormValues) => {
    setContactError(null);

    try {
      // The incorporation request should already exist at this point
      const incorporationRequestId = data?.incorporationRequest?.id;

      if (!incorporationRequestId) {
        throw new Error(
          "La solicitud de incorporación no ha sido creada. Por favor, complete el paso anterior."
        );
      }

      // Prepare data for API call
      const requestData = {
        ...contactData,
        incorporation_request_id: incorporationRequestId,
        // Don't include id for new contacts
      };

      // Call API to create contact
      const response = await createOrUpdateIncorporationContact(requestData);

      // Update local state with response data
      setData((prevData) => {
        if (!prevData) {
          // If prevData is undefined, we can't create valid initial data without required fields
          // Use data from context as fallback, but this should not happen in normal flow
          if (!data?.incorporationStripePrice || !data?.path) {
            console.warn(
              "Cannot create incorporation data without required Stripe fields"
            );
            return prevData;
          }
          return {
            incorporationRequest: data.incorporationRequest || {},
            incorporationContact: [response.data],
            incorporationStripePrice: data.incorporationStripePrice,
            path: data.path,
            currentUser: data.currentUser,
          };
        }
        return {
          ...prevData,
          incorporationRequest: prevData.incorporationRequest || {},
          incorporationContact: [
            ...(prevData.incorporationContact || []),
            response.data,
          ],
          currentUser: prevData.currentUser,
          incorporationStripePrice: prevData.incorporationStripePrice,
          path: prevData.path,
        };
      });
    } catch (error) {
      // Handle errors but don't block the UI - contact remains in local state
      if (error instanceof IncorporationContactError) {
        const errorMessage =
          error.errors && error.errors.length > 0
            ? error.errors.join(", ")
            : error.message;
        setContactError(errorMessage);
      } else {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Error al guardar el contacto";
        setContactError(errorMessage);
      }
    }
  };

  // Handle delete contact - call backend and, on success, remove from local state
  // Uses contact ID to find and delete the correct contact
  const handleDeleteContact = async (contactId: string | number | undefined) => {
    setContactError(null);

    const existingContacts = data?.incorporationContact || [];
    const contactToDelete = existingContacts.find((c) => c.id === contactId);

    // If the contact has an id, attempt to delete it in the backend
    if (contactToDelete?.id) {
      try {
        await deleteIncorporationContact(contactToDelete.id);
      } catch (error) {
        // Handle errors but don't update local state if backend delete failed
        if (error instanceof IncorporationContactError) {
          const errorMessage =
            error.errors && error.errors.length > 0
              ? error.errors.join(", ")
              : error.message;
          setContactError(errorMessage);
        } else {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Error al eliminar el contacto";
          setContactError(errorMessage);
        }
        return;
      }
    }

    // If there is no id or the delete succeeded, remove from local state
    setData((prevData) => {
      if (!prevData) return prevData;
      const updatedContacts = (prevData.incorporationContact || []).filter(
        (contact) => contact.id !== contactId
      );
      return {
        ...prevData,
        incorporationRequest: prevData.incorporationRequest || {},
        incorporationContact: updatedContacts,
        currentUser: prevData.currentUser,
        incorporationStripePrice: prevData.incorporationStripePrice,
        path: prevData.path,
      };
    });
  };

  // Handle final submit - validate and proceed to next step
  const handleSubmit = () => {
    setValidationError(null);
    // Validate using schema with current global state
    const result = shareholdersStepSchema.safeParse({
      // TODO en teoria este paso no revisa las opciones de gestion_directores
      gestion_directores: gestionDirectores,
      contacts: (data?.incorporationContact || []).map((c) => ({
        ...c,
        porcentaje_beneficio: c.porcentaje_beneficio || 0,
      })),
      user_type: data?.incorporationRequest?.user_type,
    });
    if (!result.success) {
      // Extract error message
      const error = result.error.issues[0];
      setValidationError(
        t(error?.message) || t("shareholders.validation.validation_error")
      );
      return;
    }

    // Data is already saved via API calls, just proceed to next step
    next();
  };
  // Check ALL contacts (not just shareholders) for soy_quien_completa_registro
  const shouldHideSoyQuienCompletaRegistro = (data?.incorporationContact || []).some(
    (contact) => contact.soy_quien_completa_registro
  );

  // Determine if shareholder percentages already sum to 100%
  const hasFullShareholdingCoverage = useMemo(() => {
    const total = contacts.reduce(
      (sum, c) => sum + (c?.porcentaje_beneficio || 0),
      0
    );
    return Math.abs(total - 100) <= 0.01;
  }, [contacts]);

  const canAddMoreContacts = !hasFullShareholdingCoverage;

  const modalTitle = t("shareholders.contact_form.title_add");

  return (
    <Layout
      title={t("shareholders.title")}
      description={<Trans i18nKey="shareholders.description" />}
      maxWidth={950}
    >
      <div className="d-flex flex-column gap-4 text-start mt-4">
        {/* Display list of contacts */}
        {contacts.length > 0 && (
          <div className="row g-3">
            {contacts.map((contact) => (
              <ContactCard
                key={contact?.id || contact.email}
                contact={contact}
                modalTitle={modalTitle}
                onDelete={() => handleDeleteContact(contact.id)}
                renderForm={(handlers) => (
                  <ShareholderForm
                    initialData={contact}
                    hideSoyQuienCompletaRegistro={
                      shouldHideSoyQuienCompletaRegistro
                    }
                    userType={data?.incorporationRequest?.user_type}
                    onSave={(contactData: ContactFormValues) => {
                      handleEditContact(contact.id)(contactData);
                      handlers.onClose();
                    }}
                    onCancel={handlers.onClose}
                  />
                )}
              />
            ))}
          </div>
        )}

        <div className="d-flex flex-column gap-3">
          <hr />
          {canAddMoreContacts && (
            <CustomModal
              title={modalTitle}
              size="md"
              trigger={
                <button
                  type="button"
                  className="btn btn-gray-1000 px-4 py-3 my-2 w-100 text-start font-weight-600"
                >
                  +&nbsp;
                  {t("shareholders.add_contact.button")}
                </button>
              }
            >
              {(handlers) => (
                <ShareholderForm
                  userType={data?.incorporationRequest?.user_type}
                  hideSoyQuienCompletaRegistro={
                    shouldHideSoyQuienCompletaRegistro
                  }
                  // disabledCargos={getDisabledCargos()}
                  onSave={(contactData: ContactFormValues) => {
                    handleAppendContact(contactData);
                    handlers.onClose();
                  }}
                  onCancel={handlers.onClose}
                />
              )}
            </CustomModal>
          )}

          {/* Show validation errors */}
          {validationError && (
            <div className="text-danger">{validationError}</div>
          )}

          {/* Show contact errors */}
          {contactError && (
            <div className="alert alert-danger" role="alert">
              {contactError}
            </div>
          )}
        </div>

        {/* Continue Button */}
        <div className="d-flex justify-content-center w-100 w-md-50 mx-auto">
          <NextButton
            type="button"
            label={t("company_name.continue")}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </Layout>
  );
};

ShareholdersStep.title = (t) => t("shareholders.title");
ShareholdersStep.description = (t) =>
  t("shareholders.description_progress_panel");
ShareholdersStep.isComplete = (data: IncorporationWizardInitialData) => {
  const schemaData = {
    gestion_directores: data.incorporationRequest?.gestion_directores,
    contacts: data.incorporationContact,
    user_type: data.incorporationRequest?.user_type,
  };
  const result = shareholdersStepSchema.safeParse(schemaData);
  return result.success;
};

export default ShareholdersStep;
